import React from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Armchair, Fingerprint, ScanSearch, Scissors, Clock, Ticket, CheckCircle2,
  AlertTriangle, PlayCircle, UserRound, Link2,
} from "lucide-react";
import { toast } from "sonner";
import { useBranding } from "@/context/BrandingContext";

function fmtTime(d) {
  try { return new Date(d).toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" }); }
  catch { return String(d); }
}
const addMin = (d, m) => new Date(new Date(d).getTime() + m * 60000);

// One service, followed from the seat sitting down through to the POS sale.
export default function ServiceTimeline({ event, open, onOpenChange, onWatch }) {
  const { money, branding } = useBranding();
  if (!event) return null;
  const brand = branding.primary_color;
  const start = event.started_at;
  const dur = event.duration_min || 0;
  const stage1 = Math.min(6, dur);
  const saleEng = Math.min(15, dur);
  const end = event.ended_at || addMin(start, dur);
  const matched = !!event.pos_transaction_id;

  const steps = [
    { icon: Armchair, color: "#38BDF8", title: "Seat occupied", time: fmtTime(start),
      sub: `${event.chair_label} · weight over gate — load-cell occupancy begins` },
    { icon: Fingerprint, color: "#38BDF8", title: "Weight signature captured", time: fmtTime(start),
      sub: "Calibrated occupant signature recorded (used to stitch a salon journey across chairs)" },
    { icon: ScanSearch, color: "#F59E0B", title: "Engagement (Stage 1)", time: fmtTime(addMin(start, stage1)),
      sub: `~${stage1} min in — potential service; video pre-buffer starts retaining` },
    { icon: Scissors, color: brand, title: "Service registered (sale-engagement)", time: fmtTime(addMin(start, saleEng)),
      sub: `~${saleEng} min in — eligible for reconciliation` },
    { icon: Clock, color: "#94A3B8", title: "Service complete", time: fmtTime(end),
      sub: `${dur} min · ${event.classification} · expected ${money(event.expected_price)}` },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="block w-[calc(100vw-1.5rem)] sm:w-full max-w-lg p-0 gap-0 bg-[#1E2638] border-[#2A3550]/70 overflow-hidden" data-testid="service-timeline">
        <DialogTitle className="sr-only">Service timeline — {event.chair_label}</DialogTitle>
        <div className="px-6 py-5 border-b border-[#2A3550]">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Service timeline</div>
          <h2 className="mt-1 font-display text-lg font-medium text-white">
            #{event.store_number} · {event.chair_label}
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">From the seat sitting down through to the POS sale.</p>
          <button
            data-testid="timeline-copy-link"
            onClick={() => {
              const url = `${window.location.origin}/reconciliation?event=${event.id}&key=colosseum`;
              navigator.clipboard.writeText(url).then(
                () => toast.success("Timeline link copied — send it to a manager to review"),
                () => toast.error("Couldn't copy the link"),
              );
            }}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-[#2A3550] bg-[#171E2E] px-3 py-1.5 text-xs text-slate-200 hover:border-[#38BDF8] hover:text-white transition-colors duration-200"
          >
            <Link2 className="h-3.5 w-3.5" /> Copy shareable link
          </button>
        </div>

        <div className="px-6 py-5 max-h-[55vh] overflow-y-auto">
          <div className="relative pl-6">
            <div className="absolute left-[9px] top-1 bottom-6 w-px bg-[#2A3550]" />
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="relative pb-5" data-testid={`timeline-step-${i}`}>
                  <span className="absolute -left-6 top-0 flex h-[19px] w-[19px] items-center justify-center rounded-full bg-[#171E2E] border" style={{ borderColor: s.color }}>
                    <Icon className="h-3 w-3" style={{ color: s.color }} />
                  </span>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-white">{s.title}</span>
                    <span className="text-[11px] text-slate-400 shrink-0">{s.time}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">{s.sub}</p>
                </div>
              );
            })}

            {/* POS outcome */}
            <div className="relative" data-testid="timeline-pos">
              <span className="absolute -left-6 top-0 flex h-[19px] w-[19px] items-center justify-center rounded-full bg-[#171E2E] border"
                style={{ borderColor: matched ? "#34D399" : "#EF4444" }}>
                {matched ? <CheckCircle2 className="h-3 w-3 text-emerald-400" /> : <AlertTriangle className="h-3 w-3 text-red-400" />}
              </span>
              {matched ? (
                <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/[0.06] px-4 py-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-white">
                    <Ticket className="h-4 w-4 text-emerald-400" /> Matched to POS · {event.pos_transaction_id}
                  </div>
                  <div className="mt-1 text-xs text-slate-400 flex flex-wrap gap-x-3 gap-y-1">
                    {event.pos_time && <span>Rung up {fmtTime(event.pos_time)}</span>}
                    {event.pos_cashier && <span className="inline-flex items-center gap-1"><UserRound className="h-3 w-3" /> {event.pos_cashier}</span>}
                    {event.party_size > 1 && <span>Chair {event.party_index}/{event.party_size} on shared ticket · {money(event.ticket_total)}</span>}
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-red-500/25 bg-red-500/[0.06] px-4 py-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-white">
                    <AlertTriangle className="h-4 w-4 text-red-400" /> No matching sale — flagged as revenue leakage
                  </div>
                  <div className="mt-1 text-xs text-slate-400">{money(event.expected_price)} unaccounted · clip retained for review (auto-discards once a sale is rung).</div>
                  {onWatch && (
                    <button data-testid="timeline-watch" onClick={() => onWatch(event)}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 transition-opacity duration-200"
                      style={{ backgroundColor: brand }}>
                      <PlayCircle className="h-3.5 w-3.5" /> Viewing Protocol
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
